import { useState } from "react";
import { useSearchParams } from "react-router";
import { Ban, CheckCircle2, Eye } from "lucide-react";
import { Label } from "@/components/ui/label";
import { FilterCombobox } from "../FilterCombobox";
import { filterComboboxClassName } from "./TableStyles";
import { MigrationFlagStatus, MigrationFlagStatusSelectOptions } from "../../../models/Flags/MigrationFlagStatus";

/** The address parameter an entity list reads its flag-status filter from, e.g. `?flagStatus=unresolved`. */
export const FLAG_STATUS_SEARCH_PARAM = "flagStatus";

const ICONS: Record<MigrationFlagStatus, React.ElementType> = {
  [MigrationFlagStatus.UnResolved]: Ban,
  [MigrationFlagStatus.Acknowledged]: Eye,
  [MigrationFlagStatus.Resolved]: CheckCircle2,
};

const parse = (value: string | null): MigrationFlagStatus | null =>
  Object.values(MigrationFlagStatus).includes(value as MigrationFlagStatus) ? (value as MigrationFlagStatus) : null;

/**
 * The list's flag-status filter, started from the address. It follows the address when that changes under a list
 * that stays mounted — the sidebar's link to the same list drops the parameter — and calls `onAddressChange` then,
 * so the list can go back to its first page. Picking a value in the list does not write it back to the address.
 */
export function useFlagStatusFilter(onAddressChange?: () => void) {
  const [searchParams] = useSearchParams();
  const fromAddress = parse(searchParams.get(FLAG_STATUS_SEARCH_PARAM));
  const [value, setValue] = useState<MigrationFlagStatus | null>(fromAddress);
  const [lastAddressValue, setLastAddressValue] = useState(fromAddress);

  if (fromAddress !== lastAddressValue) {
    setLastAddressValue(fromAddress);
    setValue(fromAddress);
    onAddressChange?.();
  }

  return [value, setValue] as const;
}

interface FlagStatusFilterProps {
  value: MigrationFlagStatus | null;
  onChange: (value: MigrationFlagStatus | null) => void;
}

/** Filter-bar entry for the entity's flag status, labelled the way the Flag Status column labels it. */
export function FlagStatusFilter({ value, onChange }: FlagStatusFilterProps) {
  return (
    <div className={filterComboboxClassName}>
      <Label className="text-xs font-semibold text-muted-foreground">Flag status</Label>
      <FilterCombobox
        value={value}
        onChange={(v) => onChange(v === null || v === "all" ? null : parse(v))}
        placeholder="Any"
        options={MigrationFlagStatusSelectOptions.map((o) => ({
          value: o.id,
          label: o.label,
          color: o.color,
          icon: ICONS[o.id],
        }))}
      />
    </div>
  );
}
